import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import * as Sentry from '@sentry/react';
import type { User, InsuranceCompany, UserRole } from '../types/user.types';

interface AuthState {
  user: User | null;
  token: string | null;
  company: InsuranceCompany | null;
  role: UserRole | null;
  effectivePermissions: string[];

  setAuth: (user: User, token: string, company: InsuranceCompany) => void;
  setEffectivePermissions: (perms: string[] | '*') => void;
  patchUser: (partial: Partial<User>) => void;
  clearAuth: () => void;
  isSuperAdmin: () => boolean;
  isIcAdmin: () => boolean;
  isIcBroker: () => boolean;
  hasPermission: (permission: string) => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      company: null,
      role: null,
      effectivePermissions: [],

      setAuth: (user, token, company) => {
        if (import.meta.env.PROD) {
          Sentry.setUser({ id: user.id, username: user.full_name, company_id: company?.id });
        }
        const rawRole = user.role;
        const normalizedRole: UserRole = (typeof rawRole === 'object' && rawRole !== null && 'name' in (rawRole as object))
          ? (rawRole as any).name
          : rawRole;
        set({ user, token, company, role: normalizedRole });
      },

      patchUser: (partial) => {
        const current = get().user;
        if (current) set({ user: { ...current, ...partial } });
      },

      setEffectivePermissions: (perms) => {
        if (perms === '*') {
          // Full-access role — store a sentinel so hasPermission always returns true
          set({ effectivePermissions: ['*'] });
        } else {
          set({ effectivePermissions: perms });
        }
      },

      clearAuth: () => {
        if (import.meta.env.PROD) Sentry.setUser(null);
        set({ user: null, token: null, company: null, role: null, effectivePermissions: [] });
      },

      isSuperAdmin: () => {
        const role = get().role;
        return role === 'super-admin' || role === 'admin';
      },

      isIcAdmin: () => get().role === 'ic-admin',

      isIcBroker: () => get().role === 'ic-broker',

      hasPermission: (permission: string) => {
        const perms = get().effectivePermissions;
        // '*' sentinel means full access
        if (perms.includes('*')) return true;
        return perms.includes(permission);
      },
    }),
    {
      name: 'fp-auth',
      partialize: (state) => ({ token: state.token }),
    },
  ),
);
